import type {
  ComunaEscanMasterRow,
  MasterDataUpdateBundle,
  ParticipanteMasterRow,
  ProveedorMasterRow,
  PuntosEntregaCatalogoMasterRow,
  UsuarioMasterRow,
} from '../../domain/masterDataUpdateBundle.ts';

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function asNonEmptyString(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function asFiniteInt(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'bigint' ? Number(v) : Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function asStringRecordArray(v: unknown): Record<string, unknown>[] {
  if (!Array.isArray(v)) return [];
  return v.filter(isRecord);
}

function pickStr(row: Record<string, unknown>, snake: string, camel: string): string {
  const a = asNonEmptyString(row[snake]);
  if (a != null) return a;
  const b = asNonEmptyString(row[camel]);
  return b ?? '';
}

function pickOptStr(row: Record<string, unknown>, snake: string, camel: string): string | null {
  const a = row[snake];
  if (a != null && a !== '') return String(a);
  const b = row[camel];
  if (b != null && b !== '') return String(b);
  return null;
}

function pickInt(row: Record<string, unknown>, snake: string, camel: string): number | null {
  const a = asFiniteInt(row[snake]);
  if (a != null) return a;
  return asFiniteInt(row[camel]);
}

function parseParticipanteRow(row: Record<string, unknown>): ParticipanteMasterRow | null {
  const publicId = pickStr(row, 'public_id', 'publicId') || pickStr(row, 'publicid', 'publicid');
  if (!publicId) return null;
  const tipoDocumentoPublicId = pickStr(row, 'tipo_documento_public_id', 'tipoDocumentoPublicId');
  const documento = pickStr(row, 'documento', 'documento');
  const nombre = pickStr(row, 'nombre', 'nombre');
  const telefono = pickOptStr(row, 'telefono', 'telefono') || '';
  const celular = pickOptStr(row, 'celular', 'celular') || '';
  const comunaPublicId = pickStr(row, 'comuna_public_id', 'comunaPublicId');
  if (
    !tipoDocumentoPublicId ||
    !comunaPublicId ||
    !documento ||
    !nombre
  ) {
    return null;
  }
  const tipoBonoPublicId = pickOptStr(row, 'tipo_bono_public_id', 'tipoBonoPublicId');
  const tipoBonoNombre = pickOptStr(row, 'tipo_bono_nombre', 'tipoBonoNombre');
  const estado = pickInt(row, 'estado', 'estado') ?? 1;
  return {
    publicId,
    tipoDocumentoPublicId,
    documento,
    nombre,
    telefono,
    celular,
    comunaPublicId,
    tipoBonoPublicId,
    tipoBonoNombre,
    estado: estado ? 1 : 0,
  };
}

function parseProveedorRow(row: Record<string, unknown>): ProveedorMasterRow | null {
  const publicId = pickStr(row, 'public_id', 'publicId');
  if (!publicId) return null;
  const nombre = pickStr(row, 'nombre', 'nombre');
  if (!nombre) return null;
  const comunaPublicId = pickStr(row, 'comuna_public_id', 'comunaPublicId');
  if (!comunaPublicId) return null;

  const grupoAlimentos = pickStr(row, 'grupo_alimentos', 'grupoAlimentos');
  const direccion = pickStr(row, 'direccion', 'direccion') || '';
  const estado = pickInt(row, 'estado', 'estado') ?? 1;
  const latVal = pickOptStr(row, 'latitud', 'latitud');
  const lonVal = pickOptStr(row, 'longitud', 'longitud');
  return {
    publicId,
    nombre,
    direccion,
    latitud: latVal,
    longitud: lonVal,
    comunaPublicId,
    grupoAlimentos,
    estado: estado ? 1 : 0,
  };
}

function parsePuntosEntregaCatalogoRow(row: Record<string, unknown>): PuntosEntregaCatalogoMasterRow | null {
  const publicId = pickStr(row, 'public_id', 'publicId');
  if (!publicId) return null;
  const nombre = pickStr(row, 'nombre', 'nombre');
  if (!nombre) return null;
  const comunaPublicId = pickStr(row, 'comuna_public_id', 'comunaPublicId');
  if (!comunaPublicId) return null;
  const direccion = pickStr(row, 'direccion', 'direccion') || '';
  const estado = pickInt(row, 'estado', 'estado') ?? 1;
  return {
    publicId,
    comunaPublicId,
    nombre,
    direccion,
    estado: estado ? 1 : 0,
  };
}

function parseComunaEscanRow(row: Record<string, unknown>): ComunaEscanMasterRow | null {
  const publicId = pickStr(row, 'public_id', 'publicId');
  if (!publicId) return null;
  const descripcion = pickStr(row, 'descripcion', 'descripcion');
  if (!descripcion) return null;
  const estado = pickInt(row, 'estado', 'estado') ?? 1;
  const latitud = pickOptStr(row, 'latitud', 'latitud') ?? '';
  const longitud = pickOptStr(row, 'longitud', 'longitud') ?? '';
  const coordenadasRaw = pickOptStr(row, 'coordenadas', 'coordenadas');
  const coordenadas =
    coordenadasRaw ??
    (latitud && longitud ? `${latitud},${longitud}` : latitud || longitud ? `${latitud},${longitud}` : '');
  return {
    publicId,
    descripcion,
    estado: estado ? 1 : 0,
    latitud,
    longitud,
    coordenadas,
  };
}

function parseUsuarioRow(row: Record<string, unknown>): UsuarioMasterRow | null {
  const publicId = pickStr(row, 'public_id', 'publicId');
  if (!publicId) return null;
  const nombreUsuario = pickStr(row, 'nombre_usuario', 'nombreUsuario');
  if (!nombreUsuario) return null;
  const nombre = pickStr(row, 'nombre', 'nombre') || nombreUsuario;
  const estado = pickInt(row, 'estado', 'estado') ?? 1;
  const emailRaw = pickOptStr(row, 'email', 'email');
  const pwdRaw = pickOptStr(row, 'password_hash', 'passwordHash');
  const permiso = pickOptStr(row, 'permiso', 'permiso');
  return {
    publicId,
    nombre,
    nombreUsuario,
    email: emailRaw,
    passwordHash: pwdRaw,
    permiso,
    estado: estado ? 1 : 0,
  };
}

export type ParsedMasterDataBundle = {
  bundle: MasterDataUpdateBundle;
  /** When the server sends the same value again, the client may skip re-applying the bundle. */
  catalogRevision: string | null;
};

/** Server revision for idempotent skip; year fields are metadata only (too coarse for skip). */
function pickCatalogRevision(raw: Record<string, unknown>): string | null {
  const v =
    asNonEmptyString(raw.catalog_revision) ??
    asNonEmptyString(raw.catalogRevision) ??
    asNonEmptyString(raw.revision);
  return v;
}

/**
 * Accepts a JSON string or an already-parsed object (e.g. `fetch().json()`).
 */
export function parseMasterDataJson(input: unknown): ParsedMasterDataBundle | null {
  let raw: unknown;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input);
    } catch {
      return null;
    }
  } else {
    raw = input;
  }
  if (!isRecord(raw)) return null;

  const catalogRevision = pickCatalogRevision(raw);

  const participantesIn = asStringRecordArray(raw.participantes);
  const proveedoresIn = asStringRecordArray(raw.proveedores);
  const usuariosIn = asStringRecordArray(raw.usuarios);
  const puntosEntregaIn = asStringRecordArray(raw.puntos_entrega ?? raw.puntosEntrega);
  const comunasEscanIn = asStringRecordArray(raw.comunas_esan ?? raw.comunasEscan);

  const comunasEscan: ComunaEscanMasterRow[] = [];
  for (const r of comunasEscanIn) {
    const c = parseComunaEscanRow(r);
    if (c) comunasEscan.push(c);
  }
  const participantes: ParticipanteMasterRow[] = [];
  for (const r of participantesIn) {
    const p = parseParticipanteRow(r);
    if (p) participantes.push(p);
  }
  const proveedores: ProveedorMasterRow[] = [];
  for (const r of proveedoresIn) {
    const p = parseProveedorRow(r);
    if (p) proveedores.push(p);
  }
  const usuarios: UsuarioMasterRow[] = [];
  for (const r of usuariosIn) {
    const u = parseUsuarioRow(r);
    if (u) usuarios.push(u);
  }
  const puntosEntregaCatalogo: PuntosEntregaCatalogoMasterRow[] = [];
  for (const r of puntosEntregaIn) {
    const pe = parsePuntosEntregaCatalogoRow(r);
    if (pe) puntosEntregaCatalogo.push(pe);
  }

  return {
    bundle: { comunasEscan, participantes, proveedores, usuarios, puntosEntregaCatalogo },
    catalogRevision,
  };
}
